import { formatDate, formatNumber } from "@buildpy/core/lib/format";
import { PROPOSAL_FIELD_LABEL, PROPOSAL_REVIEW_DAYS, type ProposalView } from "@buildpy/core/modules/process-standard/proposal/types";
import type { StandardProposalsView } from "@buildpy/core/views/g5a/standard-proposals";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import { sectionCardClass } from "@buildpy/ui/section-card";
import { cn } from "@buildpy/ui/cn";
import StatusBadge from "@buildpy/ui/status-badge";

import FilterChips from "@/components/filter-chips";
import { Link } from "@/lib/nav";

import {
  canVote,
  filterProposals,
  PROPOSAL_PHASE_LABEL,
  PROPOSAL_PHASE_TONE,
  PROPOSAL_PHASES,
  proposalHref,
  proposalPhase,
  proposalsHref,
  type ProposalPhase,
} from "./standards-search";

export const VOTE_LABEL = { pro: "찬성", con: "반대" } as const;

// 「1인 1일 작업량(m²) 4 → 5」.
export const changeText = (p: Pick<ProposalView, "field" | "current_value" | "proposed_value">): string =>
  `${PROPOSAL_FIELD_LABEL[p.field]} ${formatNumber(p.current_value)} → ${formatNumber(p.proposed_value)}`;

// 검토 중이면 남은 날, 마감이면 마감일 — 승인·반려는 결정일.
export const timingText = (p: ProposalView): string => {
  const phase = proposalPhase(p);
  if (phase === "reviewing") return p.days_left > 0 ? `${p.days_left}일 남음` : "오늘 마감";
  if (phase === "closed") return `${formatDate(p.review_ends_at)} 마감 · 운영자 결정 대기`;
  return `${formatDate(p.decided_at)} 결정`;
};

// 제안 머리 — 상태·항목·바꿀 숫자·제안 회사(우리 것이면 표시)·찬반 수.
export const ProposalSummary = ({ proposal }: { proposal: ProposalView }) => {
  const phase = proposalPhase(proposal);
  return (
    <div className="flex flex-col gap-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge label={PROPOSAL_PHASE_LABEL[phase]} tone={PROPOSAL_PHASE_TONE[phase]} />
        <span className="text-sm font-medium">{proposal.item_name ?? proposal.item_code}</span>
        {proposal.is_mine && <Pill tone="primary">우리 회사 제안</Pill>}
        {proposal.my_vote && <Pill>{`내 의견 · ${VOTE_LABEL[proposal.my_vote]}`}</Pill>}
      </div>
      <p className="text-sm tabular-nums">{changeText(proposal)}</p>
      <p className="text-xs text-muted-foreground tabular-nums">
        {proposal.org_name} · {timingText(proposal)} · 찬성 {proposal.pro} · 반대 {proposal.con}
      </p>
    </div>
  );
};

interface Props {
  data: StandardProposalsView;
  phase: ProposalPhase | null;
}

// 숫자 제안 목록 — 7일 공개 검토 · 상태 거르기는 URL(#386).
const ProposalsBoard = ({ data, phase }: Props) => {
  const list = filterProposals(data.proposals, phase);
  const chips = [{ label: "전체", href: proposalsHref(null) }, ...PROPOSAL_PHASES.map((p) => ({ label: PROPOSAL_PHASE_LABEL[p], href: proposalsHref(p) }))];
  return (
    <div className="flex flex-col gap-y-4">
      <p className="text-sm text-muted-foreground">
        숫자 제안은 {PROPOSAL_REVIEW_DAYS}일 동안 공개 검토를 받고, 운영자가 승인하면 다음 판에 들어갑니다. 제안은 각 항목의 「숫자 제안」 으로 올립니다.
      </p>
      <FilterChips activeHref={proposalsHref(phase)} aria-label="제안 상태" items={chips} />
      {list.length === 0 ? (
        <EmptyState>{phase ? `${PROPOSAL_PHASE_LABEL[phase]} 제안이 없습니다.` : "아직 올라온 제안이 없습니다."}</EmptyState>
      ) : (
        <ul className="flex flex-col gap-y-3" data-testid="proposal-list">
          {list.map((proposal) => (
            <li key={proposal.id}>
              <Link className={cn(sectionCardClass({ interactive: true }), "block")} href={proposalHref(proposal.id)}>
                <ProposalSummary proposal={proposal} />
                <p className="mt-2 line-clamp-2 whitespace-pre-line break-words text-sm text-muted-foreground">{proposal.reason}</p>
                {canVote(proposal) && <p className="mt-2 text-sm font-medium text-primary">찬반 남기기</p>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProposalsBoard;
